const API_URL = import.meta.env.VITE_API_URL;

export const chatService = {
  /**
   * Unified initialization - same as widget
   * Single API call to get everything needed
   */
  async initialize(botId: string, visitorId: string) {
    try {
      const response = await fetch(`${API_URL}/widget/chatbot/init?botId=${botId}&visitorId=${visitorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to initialize chatbot');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error initializing chatbot:', error);
      return null;
    }
  },

  /**
   * Start a new conversation explicitly
   */
  async startConversation(chatbotId: string, visitorId: string) {
    try {
      const response = await fetch(`${API_URL}/widget/chatbot/start-conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chatbotId, visitorId }),
      });

      if (!response.ok) {
        throw new Error('Failed to start conversation');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error starting conversation:', error);
      return null;
    }
  },

  /**
   * Unified message sender - same as widget
   * Returns immediately, all responses come via WebSocket
   */
  async sendMessage(
    conversationId: string | null,
    visitorId: string,
    botId: string,
    message: {
      type: 'text' | 'button' | 'list_reply' | 'location' | 'media';
      messageType?: string;
      text?: string;
      buttonId?: string;
      buttonLabel?: string;
      listItemId?: string;
      itemTitle?: string;
      location?: { latitude: number; longitude: number; name?: string; address?: string };
      media?: { url: string; mimeType: string; caption?: string };
      contextMessageId?: string; // For threading/replying to specific messages
    }
  ) {
    try {
      const response = await fetch(`${API_URL}/widget/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          visitorId,
          botId,
          message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Upload media file for chat
   */
  uploadMedia: async (
    file: File,
    visitorId: string,
    chatbotId: string,
    conversationId?: string
  ) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('visitorId', visitorId);
      formData.append('chatbotId', chatbotId);
      if (conversationId) {
        formData.append('conversationId', conversationId);
      }

      const response = await fetch(`${API_URL}/widget/chatbot/upload-media`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Media upload error:', error);
      throw error;
    }
  },
};
